<template>
  <TabHeader
    :title="
      $t(
        'components.structural.organization.dialog.tabs-content.information-tab.title',
      )
    "
    :subtitle="
      $t(
        'components.structural.organization.dialog.tabs-content.information-tab.subtitle',
      )
    "
  />

  <div class="my-8 flex items-end">
    <Avatar
      :image="selectedOrganization.photoURL"
      size="xlarge"
      shape="circle"
    />
    <StorageFileUpload
      :path="Paths.Organizations.Base(selectedOrganization.id)"
      file-name="photo"
      class="p-2"
      @uploaded="onAvatarUploaded"
    />
  </div>

  <FormsForm
    v-slot="{ values }"
    :schema="schema"
    :initial-values="initialValues"
    :on-submit="updateOrganization"
    :submit-label="$t('common.cta.save')"
  >
    <FormsFieldsInputText
      name="displayName"
      :label="
        $t(
          'components.structural.organization.dialog.tabs-content.information-tab.form.display-name.label',
        )
      "
      :placeholder="
        $t(
          'components.structural.organization.dialog.tabs-content.information-tab.form.display-name.placeholder',
        )
      "
    />

    <div class="grid grid-cols-12 gap-4">
      <div class="col-span-12 lg:col-span-6">
        <FormsFieldsInputText
          name="urlPath"
          :label="
            $t(
              'components.structural.organization.dialog.tabs-content.information-tab.form.url-path.label',
            )
          "
          disabled
        />
      </div>

      <div class="col-span-12 lg:col-span-6">
        <FormsFieldsInputText
          name="id"
          :label="
            $t(
              'components.structural.organization.dialog.tabs-content.information-tab.form.id.label',
            )
          "
          :placeholder="
            $t(
              'components.structural.organization.dialog.tabs-content.information-tab.form.id.placeholder',
            )
          "
          disabled
        >
          <template #suffix>
            <Button
              severity="secondary"
              class="m-0"
              @click="copyOrganizationId(values.id)"
            >
              <template #icon>
                <LucideIcon :icon="Copy" />
              </template>
            </Button>
          </template>
        </FormsFieldsInputText>
      </div>
    </div>

    <component
      :is="additionalFormFields"
      v-if="hasAdditionalFormFields"
    />
  </FormsForm>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { object, string } from 'yup'
import { Copy } from 'lucide-vue-next'
import { doc, setDoc } from '@firebase/firestore'
import { getDownloadURL } from '@firebase/storage'
import type { StorageFileUploadItem } from '~/types/components/ui/storage-file-upload'
import { useOrganizationsStore } from '~/store/organizations'
import { Collections } from '~/constants/firebase'
import { Paths } from '~/constants/firebase/storage'
import { AdditionalFormsFields } from '~/constants/components/additional'
import { getAdditionalFormFields } from '~/utils/components'
import StorageFileUpload from '~/components/ui/StorageFileUpload.vue'
import TabHeader from '~/components/ui/dialog-tabs/TabHeader.vue'
import LucideIcon from '~/components/ui/LucideIcon.vue'

const organizationsStore = useOrganizationsStore()
const { selectedOrganization } = storeToRefs(organizationsStore)

const { hasAdditionalFormFields, additionalFormFields, schema }
  = getAdditionalFormFields(
    AdditionalFormsFields.Organization.InformationTab,
    object().shape({
      id: string().required(),
      urlPath: string().required(),
      displayName: string().min(3).required(),
    }),
  )
const initialValues = {
  id: selectedOrganization.value.id,
  urlPath: selectedOrganization.value.urlPath,
  displayName: selectedOrganization.value.displayName,
  ...(hasAdditionalFormFields
    ? (() => {
        const {
          id,
          urlPath,
          displayName,
          members,
          numberOfMembers,
          photoURL,
          ...rest
        } = selectedOrganization.value
        return rest
      })()
    : {}),
}

const onAvatarUploaded = async (storageFileUploadItem: StorageFileUploadItem) => {
  const photoURL = await getDownloadURL(storageFileUploadItem.snapshot.ref)
  await updateOrganization({ photoURL })
}
const copyOrganizationId = (id: string) => {
  navigator.clipboard.writeText(id)
}
const updateOrganization = async (values) => {
  const noneEmptyValues = Object.fromEntries(
    Object.entries(values).filter(([_, value]) => value),
  )
  await setDoc(
    doc(
      useGetFirestore(),
      Collections.Organizations.Base,
      selectedOrganization.value.id,
    ),
    noneEmptyValues,
    { merge: true },
  )
}
</script>
